import React from "react";
import { useRouteError, useNavigate } from "react-router-dom";
import {
  Stack,
  Text,
  PrimaryButton,
  DefaultButton,
  Image,
  mergeStyles,
  FontWeights,
  getTheme,
  Icon,
} from "@fluentui/react";

const theme = getTheme();

const containerStyle = mergeStyles({
  minHeight: "100vh",
  backgroundColor: theme.palette.neutralLighterAlt,
  padding: "40px 20px",
});

const cardStyle = mergeStyles({
  backgroundColor: theme.palette.white,
  borderRadius: "8px",
  padding: "48px",
  boxShadow: theme.effects.elevation16,
  maxWidth: "600px",
  margin: "0 auto",
  textAlign: "center",
});

const iconStyle = mergeStyles({
  fontSize: "72px",
  color: theme.palette.red,
  marginBottom: "24px",
});

const titleStyle = mergeStyles({
  fontSize: "32px",
  fontWeight: FontWeights.bold,
  color: theme.palette.neutralPrimary,
  marginBottom: "16px",
});

const subtitleStyle = mergeStyles({
  fontSize: "18px",
  color: theme.palette.neutralSecondary,
  marginBottom: "24px",
  lineHeight: "1.5",
});

const errorCodeStyle = mergeStyles({
  fontSize: "14px",
  color: theme.palette.neutralTertiary,
  fontFamily: "monospace",
  backgroundColor: theme.palette.neutralLighter,
  padding: "12px",
  borderRadius: "4px",
  marginBottom: "32px",
  textAlign: "left",
  wordBreak: "break-all",
});

export default function ErrorPage() {
  const error = useRouteError() as any;
  const navigate = useNavigate();

  const handleGoHome = () => {
    navigate("/");
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  const getErrorMessage = () => {
    if (error?.status === 404) {
      return {
        title: "Page Not Found",
        message: "The page you're looking for doesn't exist or may have been moved.",
        icon: "PageRemove",
      };
    } else if (error?.status >= 500) {
      return {
        title: "Server Error",
        message: "We're experiencing technical difficulties. Please try again later.",
        icon: "ErrorBadge",
      };
    } else {
      return {
        title: "Something Went Wrong",
        message: "An unexpected error occurred. We apologize for the inconvenience.",
        icon: "Warning",
      };
    }
  };

  const errorInfo = getErrorMessage();

  return (
    <div className={containerStyle}>
      <Stack horizontalAlign="center" verticalAlign="center" style={{ minHeight: "100vh" }}>
        <div className={cardStyle}>
          <Stack tokens={{ childrenGap: 16 }} horizontalAlign="center">
            {/* Error Icon */}
            <Icon iconName={errorInfo.icon} className={iconStyle} />
            
            {/* Error Title */}
            <Text className={titleStyle}>
              {errorInfo.title}
            </Text>
            
            {/* Error Message */}
            <Text className={subtitleStyle}>
              {errorInfo.message}
            </Text>
            
            {/* Error Details (if available) */}
            {error && (
              <Stack tokens={{ childrenGap: 8 }} style={{ width: "100%" }}>
                <Text variant="medium" style={{ fontWeight: FontWeights.semibold }}>
                  Error Details:
                </Text>
                <div className={errorCodeStyle}>
                  <div><strong>Status:</strong> {error.status || "Unknown"}</div>
                  <div><strong>Status Text:</strong> {error.statusText || "No additional information"}</div>
                  {error.data && (
                    <div><strong>Message:</strong> {error.data}</div>
                  )}
                  {error.error?.message && (
                    <div><strong>Details:</strong> {error.error.message}</div>
                  )}
                </div>
              </Stack>
            )}
            
            {/* Action Buttons */}
            <Stack horizontal tokens={{ childrenGap: 12 }} style={{ marginTop: "24px" }}>
              <PrimaryButton
                text="Go to Home"
                iconProps={{ iconName: "Home" }}
                onClick={handleGoHome}
              />
              <DefaultButton
                text="Go Back"
                iconProps={{ iconName: "Back" }}
                onClick={handleGoBack}
              />
              <DefaultButton
                text="Refresh Page"
                iconProps={{ iconName: "Refresh" }}
                onClick={handleRefresh}
              />
            </Stack>
            
            {/* Additional Help */}
            <Stack tokens={{ childrenGap: 8 }} style={{ marginTop: "32px" }}>
              <Text variant="small" style={{ color: theme.palette.neutralSecondary }}>
                If this problem persists, please contact support.
              </Text>
              <Stack horizontal tokens={{ childrenGap: 16 }} horizontalAlign="center">
                <DefaultButton
                  text="Contact Support"
                  iconProps={{ iconName: "Help" }}
                  styles={{ root: { minWidth: "auto" } }}
                  onClick={() => window.open("mailto:support@yourcompany.com", "_blank")}
                />
                <DefaultButton
                  text="Report Issue"
                  iconProps={{ iconName: "Bug" }}
                  styles={{ root: { minWidth: "auto" } }}
                  onClick={() => window.open("https://github.com/yourrepo/issues", "_blank")}
                />
              </Stack>
            </Stack>
          </Stack>
        </div>
      </Stack>
    </div>
  );
}